import { withBackend } from '../utils/api';

class ChatService {
  constructor(apiKey, knowledgeService) {
    this.apiKey = apiKey;
    this.knowledgeService = knowledgeService;
  }

  async sendMessage(message, presentationAnalysis = null, chatHistory = [], onDelta, attachments = []) {
    let aggregated = '';

    await this.streamMessage(
      message,
      presentationAnalysis,
      chatHistory,
      (delta) => {
        aggregated += delta;
        if (typeof onDelta === 'function') {
          onDelta(delta, aggregated);
        }
      },
      attachments
    );

    return {
      content: aggregated,
      timestamp: new Date().toISOString(),
    };
  }

  async streamMessage(message, presentationAnalysis = null, chatHistory = [], onDelta, attachments = []) {
    if (!this.apiKey) {
      throw new Error('OpenAI API key is not configured.');
    }

    try {
      console.log('[ChatService] Building context...');
      const attachmentFiles = Array.isArray(attachments) ? attachments : [];
      const context = this.knowledgeService.buildChatContext(message, presentationAnalysis, chatHistory);
      const attachmentContextNote = attachmentFiles.length
        ? `

[User attached ${attachmentFiles.length} reference image(s): ${attachmentFiles
            .map((file) => file?.name || 'image')
            .join(', ')}]`
        : '';
      const decoratedMessage = this.decorateUserMessage(`${message}${attachmentContextNote}`, context);
      const systemMessage = this.createSystemMessage(context);
      const focus = (presentationAnalysis && presentationAnalysis.analysisFocus) || 50;

      const chatUrl = withBackend('/chat');
      console.log('[ChatService] Sending request to:', chatUrl);

      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 120000); // 120 second timeout

      const useFormData = attachmentFiles.length > 0;
      let body;
      let headers = {};
      if (useFormData) {
        const formData = new FormData();
        formData.append('api_key', this.apiKey);
        formData.append('message', decoratedMessage);
        formData.append('analysis', JSON.stringify(presentationAnalysis || {}));
        formData.append('context', JSON.stringify(context));
        formData.append('system_prompt', systemMessage.content);
        formData.append('conversation_history', JSON.stringify(chatHistory));
        formData.append('analysis_focus', focus);
        attachmentFiles.forEach((file, index) => {
          if (file) {
            formData.append('attachments', file, file.name || `attachment-${index + 1}.png`);
          }
        });
        body = formData;
      } else {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify({
          api_key: this.apiKey,
          message: decoratedMessage,
          analysis: presentationAnalysis,
          context,
          system_prompt: systemMessage.content,
          conversation_history: chatHistory,
          analysis_focus: focus,
        });
      }

      const response = await fetch(chatUrl, {
        method: 'POST',
        headers,
        body,
        signal: controller.signal,
      });

      clearTimeout(timeout);
      console.log('[ChatService] Response received:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Chat failed (${response.status})`);
      }

      if (!response.body) {
        throw new Error('Streaming response unsupported in this environment.');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        let boundary = buffer.indexOf('\n\n');
        while (boundary !== -1) {
          const rawEvent = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          this.handleSSEEvent(rawEvent, onDelta);
          boundary = buffer.indexOf('\n\n');
        }
      }
    } catch (error) {
      console.error('Chat Service Streaming Error:', error);
      throw error;
    }
  }

  handleSSEEvent(rawEvent, onDelta) {
    const lines = rawEvent.split('\n');
    let eventType = 'message';
    const dataLines = [];

    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventType = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trim());
      }
    }

    const dataPayload = dataLines.join('\n');

    if (eventType === 'error') {
      if (dataPayload) {
        try {
          const parsed = JSON.parse(dataPayload);
          throw new Error(parsed.error || 'Unknown streaming error');
        } catch {
          throw new Error(dataPayload);
        }
      }
      throw new Error('Unknown streaming error');
    }

    if (dataPayload === '[DONE]') {
      return;
    }

    if (!dataPayload) {
      return;
    }

    try {
      const parsed = JSON.parse(dataPayload);
      if (parsed.error) {
        throw new Error(parsed.error);
      }
      if (parsed.delta && typeof onDelta === 'function') {
        onDelta(parsed.delta);
      }
    } catch (error) {
      console.error('Failed to parse streaming payload:', dataPayload, error);
    }
  }

  createSystemMessage(context) {
    return {
      role: 'system',
      content: `You are a senior presentation design consultant and expert in "The Presentation Design Compass" methodology. You help designers make strategic design decisions for client presentations.
  
  CONTEXT PROVIDED:
  ${context}
  
  INSTRUCTIONS:
  - Answer design questions with specific, actionable advice based on ACTUAL slide content and visuals
  - When thumbnails are available (Thumbnail=YES), reference the actual visual appearance you can see
  - Always consider the presentation type and Design Compass framework
  - Reference company guidelines and design documents when relevant
  - For specific slide questions, analyze both the text content AND visual elements
  - Give reasoning based on design principles, psychology, and the specific presentation context
  - Keep responses conversational but professional
  - If you need more information to give a good answer, ask clarifying questions
  - Always tie recommendations back to the presentation objectives and audience
  
  VISUAL ANALYSIS CAPABILITIES:
  - You can see slide thumbnails when available and should reference them in your analysis
  - Consider layout, color schemes, visual hierarchy, and content density you observe
  - Compare what you see visually with brand guidelines provided in the knowledge base
  - Assess visual-text balance and overall design effectiveness
  
  DESIGN COMPASS METHODOLOGY TO FOLLOW:
  1. Context Understanding - Consider why this presentation exists
  2. Ambition Setting - Align with presentation goals
  3. Design Decisions - Make strategic visual choices
  4. Storytelling - Support the narrative flow
  5. Work Justification - Provide clear reasoning
  
  Remember: Every design decision should serve the presentation's strategic purpose, not just look good. When you can see the slides, provide specific visual feedback about colors, fonts, layouts, and overall design quality.`
    };
  }

  decorateUserMessage(message, context) {
    const lower = (message || '').toLowerCase();
    const comparative = [
      'which slide needs the most work',
      'worst slide',
      'needs the most improvement',
      'biggest issue slide',
      'top slides to fix'
    ].some((k) => lower.includes(k));

    if (!comparative) return message;

    return `${message}

Note: Use the SLIDE QUALITY SUMMARY in the context to answer with a ranked list (top 3) and specific reasons drawn from the summary and analysis. Provide slide numbers and concrete next actions.`;
  }
  
  suggestQuestions(presentationAnalysis) {
    if (!presentationAnalysis) {
      return [
        "What presentation type should I focus on for this content?",
        "How can I better understand my audience?",
        "What design direction would work best?",
        "What questions should I ask my client?"
      ];
    }
  
    const type = presentationAnalysis.presentationType?.primary;
    const suggestions = [];
  
    switch (type) {
      case 'Executive & Strategic':
        suggestions.push(
          "Should I use more charts or text for executive audiences?",
          "How formal should my color palette be?",
          "What font size works best for boardroom presentations?"
        );
        break;
      case 'Sales & Influence':
        suggestions.push(
          "How can I make my value proposition more visually compelling?",
          "Should I use dark or light backgrounds for impact?",
          "What imagery style would be most persuasive?"
        );
        break;
      case 'Engagement & Immersion':
        suggestions.push(
          "How can I make this presentation more emotionally engaging?",
          "What visual metaphors would work for my story?",
          "Should I use more imagery or illustration?"
        );
        break;
      case 'Informative & Educational':
        suggestions.push(
          "How can I make complex information easier to understand?",
          "What's the best way to structure my content flow?",
          "Should I use more diagrams or bullet points?"
        );
        break;
      default:
        suggestions.push(
          "How can I improve the visual hierarchy in my slides?",
          "What's the best way to handle bullet points?",
          "How do I know if my design matches the presentation objective?"
        );
        break;
    }
  
    if (presentationAnalysis.executionGuidance?.priorityFixes?.length > 0) {
      suggestions.push(
        `How should I address: ${presentationAnalysis.executionGuidance.priorityFixes[0]}?`
      );
    }
  
    if (presentationAnalysis.designDirection?.colors?.primary?.length > 0) {
      suggestions.push(
        `Should I use ${presentationAnalysis.designDirection.colors.primary[0]} as my primary color?`
      );
    }
  
    suggestions.push(
      "How can I improve the visual hierarchy in my slides?",
      "What's the best way to handle bullet points?",
      "How do I know if my design matches the presentation objective?"
    );
  
    return suggestions.slice(0, 6);
  }
  
  getQuickTips(presentationType) {
    const tips = {
      'Executive & Strategic': [
        "Keep layouts clean and structured",
        "Use navy, gray, or professional colors",
        "Lead with key insights, not details",
        "Ensure all text is readable from distance"
      ],
      'Sales & Influence': [
        "Use bold, contrasting colors",
        "Lead with benefits, not features",
        "Include compelling visuals and stories",
        "Create clear calls-to-action"
      ],
      'Engagement & Immersion': [
        "Use full-screen imagery and visuals",
        "Incorporate brand colors and energy",
        "Tell stories with visual metaphors",
        "Keep text minimal, visuals maximum"
      ],
      'Informative & Educational': [
        "Use consistent layouts and structures",
        "Break complex info into digestible chunks",
        "Use diagrams and charts effectively",
        "Ensure clear information hierarchy"
      ]
    };
  
    return tips[presentationType] || tips['Executive & Strategic'];
  }
}
  
export default ChatService;

